import { Css, FormSectionLayout, Tag, useComputed, useTestIds } from "@homebound/beam";
import { useMemo } from "react";
import { Price } from "src/components/Price";
import { ChangeEventWorkflow } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { BaseChangeEventFormState } from "src/routes/projects/change-events/guided/baseChangeEventForm";
import { StatTotals } from "src/routes/projects/change-events/guided/components/StatTotals";
import { changeEventQueryFor } from "src/routes/projects/change-events/guided/utils";
import { ProjectParams } from "src/routes/routesDef";
import { fail, formatCentsToPrice, queryResult } from "src/utils";
import { formatOptionPrice } from "../components/OptionPrice";
import { ReviewImpactedPrice } from "../components/ReviewImpactedPrice";
import {
  ReviewConfigurationStep_ConfigChangeFragment,
  useReviewConfigurationStepQuery,
} from "./ReviewConfigurationStep.generated";
import { ReviewConfigurationGroup, toChangedGroupIds, toReviewConfigurationSections } from "./reviewConfigurationUtils";

type ReviewConfigurationStepProps<F> = { formState: BaseChangeEventFormState<F> };

/**
 * A completely read-only breakdown of the configuration the project would end up with, so the user can confirm it
 * before Review Impact.
 */
export function ReviewConfigurationStep<F>(props: ReviewConfigurationStepProps<F>) {
  const { formState } = props;
  const { projectId } = useRouteParams<ProjectParams>();
  // Without a signed contract step 1 only dry runs the save, so the change event lives in the cache alone
  const { changeEventId, fetchPolicy } = useComputed(() => changeEventQueryFor(formState), [formState]);
  const query = useReviewConfigurationStepQuery({
    variables: { structureId: projectId, changeEventId: changeEventId ?? fail("No change event ID") },
    fetchPolicy,
  });
  return queryResult(query, ({ changeEvent }) => (
    <ReviewConfigurationStepView
      formState={formState}
      configChange={changeEvent.configChange ?? fail("No config change")}
    />
  ));
}

type ReviewConfigurationStepViewProps<F> = ReviewConfigurationStepProps<F> & {
  configChange: ReviewConfigurationStep_ConfigChangeFragment;
};

function ReviewConfigurationStepView<F>(props: ReviewConfigurationStepViewProps<F>) {
  const { formState, configChange } = props;
  const { result, original } = configChange;
  const comparesToOriginal = useComputed(
    // An initial configuration replaces everything, so there is no previous config worth comparing it to
    () => formState.workflow.value !== ChangeEventWorkflow.ApplyInitialConfiguration,
    [formState],
  );

  const changedGroupIds = useMemo(
    () => (comparesToOriginal ? toChangedGroupIds(configChange) : new Set<string>()),
    [comparesToOriginal, configChange],
  );
  const sections = useMemo(() => toReviewConfigurationSections(result, changedGroupIds), [result, changedGroupIds]);

  return (
    <FormSectionLayout
      title="Review Configuration"
      description="Review the resulting configuration and prices below."
      initialFields={
        comparesToOriginal && (
          <ConfigurationTotals
            newTotalInCents={result.config.reservedTotalPriceInCents}
            previousTotalInCents={original.config.reservedTotalPriceInCents}
          />
        )
      }
      withJumpLinks
      sections={sections.map((section, i) => ({
        title: section.title,
        fields: <ConfigReviewSection groups={section.groups} showColumnLabels={i === 0} />,
      }))}
    />
  );
}

type ConfigurationTotalsProps = { newTotalInCents: number; previousTotalInCents: number };

function ConfigurationTotals(props: ConfigurationTotalsProps) {
  const { newTotalInCents, previousTotalInCents } = props;
  return (
    <StatTotals
      testId="configurationTotals"
      items={[
        {
          value: (
            <span css={Css.blue600.$}>
              <Price valueInCents={newTotalInCents} />
            </span>
          ),
          label: "New Configuration Total",
          testId: "newTotal",
        },
        {
          value: <ReviewImpactedPrice valueInCents={newTotalInCents - previousTotalInCents} showZeroAsNone />,
          label: "Δ from Previous",
          testId: "delta",
        },
      ]}
    />
  );
}

type ConfigReviewSectionProps = { groups: ReviewConfigurationGroup[]; showColumnLabels: boolean };
function ConfigReviewSection(props: ConfigReviewSectionProps) {
  const { groups, showColumnLabels } = props;
  const tid = useTestIds({}, "configReviewGroup");
  return (
    <>
      {showColumnLabels && (
        <div css={Css.df.jcsb.sm.gray700.$} {...tid.columnLabels}>
          <span>Selection</span>
          <span>New Prices</span>
        </div>
      )}
      {groups.map((group) => (
        <div css={Css.df.fdc.gap1.pb2.bb.bcGray200.$} {...tid} key={group.title}>
          <div css={Css.df.aic.jcsb.gap2.$}>
            <div css={Css.df.aic.gap1.$}>
              <span css={Css.mdSb.gray900.$} {...tid.title}>
                {group.title}
              </span>
              {group.isChanged && <Tag type="warning" icon="error" text="Changed" {...tid.changedTag} />}
            </div>
            <span css={Css.mdSb.gray900.wsnw.$} {...tid.amount}>
              {formatGroupAmount(group.amountInCents)}
            </span>
          </div>
          {group.items.map((item) => (
            <div key={item.title} css={Css.df.aic.jcsb.gap2.$}>
              <span css={Css.sm.gray900.$} {...tid.itemTitle}>
                {item.title}
              </span>
              {item.amountInCents !== undefined && (
                <span css={Css.sm.gray900.wsnw.$} {...tid.itemAmount}>
                  {formatOptionPrice(item.amountInCents, { trim: true })}
                </span>
              )}
            </div>
          ))}
        </div>
      ))}
    </>
  );
}

function formatGroupAmount(amountInCents: number): string {
  return amountInCents === 0 ? "$0" : formatCentsToPrice(amountInCents, { trim: true });
}
